import {
  CountryCode,
  convertCountryCodeToCountryName,
  FormType,
  nameValidationRegex,
  OfferedFulfillmentMethod,
  PaymentMethod,
  resolveCheckoutFulfillmentMethod,
} from "@simplyk/common";
import { useElements } from "@stripe/react-stripe-js";
import { UseFormSetValue } from "react-hook-form";

import { paymentMethodCollectsBuyerBilling } from "@/canvas/features/FormV2/steps/step2/hooks/useBuyerInformationRenderPosition";
import { validateFulfillmentCheckout } from "@/common/helpers/fulfillmentCheckout";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DonationFormPaymentInput } from "@/common/types/donationForm";
import { TicketingPaymentInput } from "@/common/types/ticketing";

export const unknownPaymentErrorCode = {
  card1: { code: 901, description: "Stripe was not loaded when trying to pay: can't use stripe payment element" },
  card2: {
    code: 902,
    description: "Command was not returned from Zeffy server, payment is not succeeded, a dev can check",
  },
  card3: {
    code: 903,
    description: "Payment created but error getting return URL failed for fraudulent transaction",
  },
  card4: { code: 904, description: "Fail confirming payment on Stripe side, payment is not succeeded" },
  card5: {
    code: 905,
    description: "Payment intent on stripe is no longer valid and need a new payment method, payment is not succeeded",
  },
  card6: {
    code: 906,
    description: "Error when confirming payment on stripe: payment is not succeeded, a dev should take a look",
  },
  card7: { code: 907, description: "Unknown error: this error need to be looked at by dev" },
  card8: {
    code: 908,
    description: "Missing return URL: card payment is succeeded but thank you page could not be reached",
  },
  pad1: {
    code: 801,
    description: "Payment created but error getting return URL failed for fraudulent transaction",
  },
  pad2: {
    code: 802,
    description: "Command was not returned from Zeffy server, payment is not succeeded, a dev can check",
  },
  pad3: { code: 803, description: "Fail confirming payment on Stripe side, payment is not succeeded" },
  pad4: {
    code: 804,
    description: "Missing return URL: pad payment is succeeded but thank you page could not be reached",
  },
  cashApp1: {
    code: 701,
    description: "Command was not returned from Zeffy server, payment is not succeeded, a dev can check",
  },
  cashApp2: { code: 702, description: "Fail confirming payment on Stripe side, payment is not succeeded" },
  other1: { code: 201, description: "The payment error has no specific code" },
  other2: { code: 202, description: "The tip is negative: can't process it" },
  other3: { code: 203, description: "Unknown error on command submission on Zeffy side: a dev should take a look" },
  other4: {
    code: 204,
    description:
      "Error when creating registration campaigns. Payment is succeeded but will not display thank you page.",
  },
  other5: {
    code: 205,
    description: "Should not create registration campaigns. Payment is succeeded but will not display thank you page.",
  },
  offline1: {
    code: 601,
    description: "Command was not returned from Zeffy server, payment is not succeeded, a dev can check",
  },
  offline2: {
    code: 602,
    description: "Missing return URL: offline payment is succeeded but thank you page could not be reached",
  },
};

export type PaymentStep =
  | "form-validation"
  | "address-validation"
  | "stripe-load"
  | "stripe-element-submit"
  | "create-payment-method"
  | "server-submit"
  | "handle-next-action"
  | "manual-confirmation"
  | "post-submit";

export interface PaymentErrorReport {
  rawErrorMessage: string;
  rawErrorCode: string;
  step: PaymentStep;
  paymentMethod: string | undefined;
  isExpressCheckout: boolean;
  commandId?: string | null;
  isPaymentSucceeded?: boolean;
  isKnownError: boolean;
  declineCode?: string;
}

// Issuer refusals the payer can usually unblock by asking their bank to allow the payment (often a foreign online one).
const DECLINE_CODES_SOLVED_BY_CONTACTING_BANK = new Set([
  "authentication_required",
  "do_not_honor",
  "generic_decline",
  "transaction_not_allowed",
]);

export const isDeclineSolvedByContactingBank = (declineCode: string | undefined): boolean =>
  Boolean(declineCode && DECLINE_CODES_SOLVED_BY_CONTACTING_BANK.has(declineCode));

type PaypalCapturedPayer = { email?: string | null; firstName?: string | null; lastName?: string | null };

export const withPaypalCapturedBuyer = <T extends PaypalCapturedPayer>(
  command: T,
  payer: PaypalCapturedPayer | null | undefined
): T => ({
  ...command,
  email: payer?.email ?? command.email,
  firstName: payer?.firstName ?? command.firstName,
  lastName: payer?.lastName ?? command.lastName,
});

export type ValidateAddressParams = {
  elements: ReturnType<typeof useElements>;
  paymentInput: DonationFormPaymentInput | TicketingPaymentInput;
  t: ReturnType<typeof useTranslate>["t"];
  handlePaymentError: (report: PaymentErrorReport) => void;
  selectedPaymentMethod: string;
  isExpressCheckout: boolean;
  commandId: string | null;
  setValue: UseFormSetValue<DonationFormPaymentInput | TicketingPaymentInput>;
};

export const validateAndExtractAddress = async ({
  elements,
  paymentInput,
  t,
  handlePaymentError,
  selectedPaymentMethod,
  isExpressCheckout,
  commandId,
  setValue,
}: ValidateAddressParams): Promise<{ shouldEarlyReturn: boolean }> => {
  if (isExpressCheckout) {
    return { shouldEarlyReturn: false };
  }

  const addressElement = elements?.getElement?.("address");
  const addressValue = await addressElement?.getValue?.();

  if (!addressValue) {
    return { shouldEarlyReturn: false };
  }

  if (!addressValue.complete) {
    // PayPal/Venmo are not handled by Stripe Elements — skip submit() for them
    if (selectedPaymentMethod !== PaymentMethod.PayPal && selectedPaymentMethod !== PaymentMethod.Venmo) {
      await elements?.submit();
    }
    return { shouldEarlyReturn: true };
  }

  const { city, country, line1, line2, postal_code, state } = addressValue.value.address;
  const { firstName, lastName } = addressValue.value;

  const isFirstNameValid = !firstName || nameValidationRegex.test(firstName);
  const isLastNameValid = !lastName || nameValidationRegex.test(lastName);

  if (!isFirstNameValid || !isLastNameValid) {
    handlePaymentError({
      rawErrorCode: "invalid_name_characters",
      rawErrorMessage: t("common", "name.invalidCharacterUsed"),
      step: "address-validation",
      paymentMethod: selectedPaymentMethod,
      isExpressCheckout,
      commandId,
      isKnownError: true,
    });
    return { shouldEarlyReturn: true };
  }

  paymentInput.address = line2 ? `${line1} ${line2}` : line1;
  paymentInput.city = city;
  paymentInput.postalCode = postal_code;
  paymentInput.region = state;
  paymentInput.country = convertCountryCodeToCountryName(country as CountryCode);
  paymentInput.firstName = firstName;
  paymentInput.lastName = lastName;

  setValue("firstName", firstName);
  setValue("lastName", lastName);

  return { shouldEarlyReturn: false };
};

export const validateFulfillmentCheckoutForSubmit = ({
  paymentInput,
  formType,
  t,
  handlePaymentError,
  selectedPaymentMethod,
  isExpressCheckout,
  commandId,
  requireFulfillmentCheckout,
  canOfferSameAsBilling,
  offeredMethods,
}: {
  paymentInput: DonationFormPaymentInput | TicketingPaymentInput;
  formType: FormType;
  t: ReturnType<typeof useTranslate>["t"];
  handlePaymentError: (report: PaymentErrorReport) => void;
  selectedPaymentMethod: string;
  isExpressCheckout: boolean;
  commandId: string | null;
  requireFulfillmentCheckout: boolean;
  canOfferSameAsBilling: boolean;
  offeredMethods: OfferedFulfillmentMethod[];
}): boolean => {
  if (formType !== FormType.Ticketing) {
    return false;
  }

  const ticketingInput = paymentInput as TicketingPaymentInput;

  const resolvedMethod = resolveCheckoutFulfillmentMethod({
    offeredMethods,
    selectedMethod: ticketingInput.fulfillmentMethod,
  });
  if (resolvedMethod) {
    ticketingInput.fulfillmentMethod = resolvedMethod;
  }

  const fulfillmentErrorKey = validateFulfillmentCheckout(ticketingInput, {
    isActive: requireFulfillmentCheckout,
    canOfferSameAsBilling:
      canOfferSameAsBilling && paymentMethodCollectsBuyerBilling(selectedPaymentMethod as PaymentMethod),
    canOfferSameFullNameAsBuyer:
      !canOfferSameAsBilling && paymentMethodCollectsBuyerBilling(selectedPaymentMethod as PaymentMethod),
  });

  if (!fulfillmentErrorKey) {
    return false;
  }

  handlePaymentError({
    rawErrorCode: "fulfillment_validation_error",
    rawErrorMessage: t("ticketing", fulfillmentErrorKey),
    step: "form-validation",
    paymentMethod: selectedPaymentMethod,
    isExpressCheckout,
    commandId,
    isKnownError: true,
  });
  return true;
};

type CashAppConfirmationResult = {
  error?: { code?: string; message?: string };
  paymentIntent?: { status: string };
  setupIntent?: { status: string };
};

export const getCashAppConfirmationFailure = (
  result: CashAppConfirmationResult
): { code: string; message: string | undefined } | null => {
  if (result.error) {
    return { code: result.error.code || "cashapp_confirm_error", message: result.error.message };
  }
  const status = result.paymentIntent?.status ?? result.setupIntent?.status;
  if (status === "succeeded" || status === "processing") {
    return null;
  }
  return { code: `cashapp_intent_${status ?? "missing"}`, message: undefined };
};
